import { useContext, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClusterNodeMetrics } from "@graylog/server-api";
import MetricsContext from "contexts/MetricsContext";
const useMetricsContext = () => {
  const context = useContext(MetricsContext);
  if (!context) {
    throw new Error("useMetrics must be used within a MetricsProvider");
  }
  return context;
};
export const useMetrics = (metricNames) => {
  const { metrics, isLoading, subscribe, unsubscribe } = useMetricsContext();
  const sortedNames = useMemo(
    () => [...metricNames].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(metricNames)]
  );
  useEffect(() => {
    if (sortedNames.length === 0) return void 0;
    subscribe(sortedNames);
    return () => {
      unsubscribe(sortedNames);
    };
  }, [sortedNames, subscribe, unsubscribe]);
  return { data: metrics, isLoading };
};
export const useNodeMetrics = (nodeId, metricNames) => {
  const { data: metrics, isLoading } = useMetrics(metricNames);
  return { data: metrics[nodeId], isLoading };
};
export const useMetric = (metricName) => {
  const names = useMemo(() => [metricName], [metricName]);
  return useMetrics(names);
};
export const useNodeMetric = (nodeId, metricName) => {
  const names = useMemo(() => [metricName], [metricName]);
  return useNodeMetrics(nodeId, names);
};
export const useMetricsNames = (nodeId, namespace = "org") => {
  const { data, isLoading } = useQuery({
    queryKey: ["metrics", "names", nodeId, namespace],
    queryFn: () => ClusterNodeMetrics.byNamespace(nodeId, namespace).then(({ metrics }) => metrics),
    enabled: nodeId !== void 0
  });
  return { data, isLoading };
};
const THROUGHPUT_INPUT = "org.graylog2.throughput.input.1-sec-rate";
const THROUGHPUT_OUTPUT = "org.graylog2.throughput.output.1-sec-rate";
const THROUGHPUT_METRICS = [THROUGHPUT_INPUT, THROUGHPUT_OUTPUT];
export const useGlobalThroughput = () => {
  const { data: metrics, isLoading } = useMetrics(THROUGHPUT_METRICS);
  const input = useMemo(
    () => Object.keys(metrics).reduce((sum, nodeId) => {
      var _a;
      const metric = (_a = metrics[nodeId]) == null ? void 0 : _a[THROUGHPUT_INPUT];
      return sum + ((metric == null ? void 0 : metric.metric) && "value" in metric.metric ? metric.metric.value : 0);
    }, 0),
    [metrics]
  );
  const output = useMemo(
    () => Object.keys(metrics).reduce((sum, nodeId) => {
      var _a;
      const metric = (_a = metrics[nodeId]) == null ? void 0 : _a[THROUGHPUT_OUTPUT];
      return sum + ((metric == null ? void 0 : metric.metric) && "value" in metric.metric ? metric.metric.value : 0);
    }, 0),
    [metrics]
  );
  return { input, output, isLoading };
};
